import type React from 'react';
import { useKeyboardNavigation } from 'app/shared/modules/filter/FilterBar/hooks';
import { useYieldCallback } from '@zillow/yield-callback';

// Tracking

// Types
interface CommuteState {
  commuteLats: number | null;
  commuteLons: number | null;
  commuteMode: string | null;
  commuteTime: number | null;
  commuteTimeMode: string | null;
}

interface UseTravelDropdownParams {
  focusRef: React.RefObject<HTMLButtonElement>;
  refList: Array<React.RefObject<HTMLLIElement>>;
  showDropdown: boolean;
  setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  filterState: CommuteState;
  setFilterState: React.Dispatch<React.SetStateAction<CommuteState>>;
  toggleDepartureDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  syncToRedux: (state: CommuteState) => void;
}

interface UseDepartureDropdownParams {
  focusRef: React.RefObject<HTMLButtonElement>;
  refList: Array<React.RefObject<HTMLLIElement>>;
  showDropdown: boolean;
  setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  filterState: CommuteState;
  setFilterState: React.Dispatch<React.SetStateAction<CommuteState>>;
  toggleTravelModeDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  syncToRedux: (state: CommuteState) => void;
}

interface UseAutocompleteDropdownParams {
  focusRef: React.RefObject<HTMLInputElement>;
  refList: Array<React.RefObject<HTMLLIElement>>;
  showDropdown: boolean;
  toggleDropdown: (show: boolean) => void;
}

export const useTravelDropdown = ({
  focusRef,
  refList,
  showDropdown,
  setShowDropdown,
  filterState,
  setFilterState,
  toggleDepartureDropdown,
  syncToRedux,
}: UseTravelDropdownParams) => {
  // General keyboard nav hook
  const [basicKeyDownNav, dropdownKeyDownNav] = useKeyboardNavigation({
    focusRef,
    refList,
    showDropdown,
    toggleDropdown: setShowDropdown,
  });
  const travelBasicKeyDownNav = basicKeyDownNav;
  const travelKeyDownNav = dropdownKeyDownNav;

  const _handleTravelSelection = (option: string) => {
    const newState = {
      ...filterState,
      commuteMode: option,
    };
    setFilterState(newState);
    syncToRedux(newState);

    setShowDropdown(false);
  };

  const handleTravelModeButtonClick = useYieldCallback(() => {
    setShowDropdown(!showDropdown);
    toggleDepartureDropdown(false);
  });

  const handleTravelModeButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => travelBasicKeyDownNav(e.keyCode);

  const handleTravelModeOptionClick = (e: React.MouseEvent<HTMLLIElement>) => {
    const option = e.currentTarget.getAttribute('data-option-value');

    // Guard against null/undefined option
    if (!option) return;

    _handleTravelSelection(option);
  };

  const handleTravelModeOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    e.stopPropagation();

    const keyCode = e.keyCode;
    const enterKeyPressed = keyCode === 13;
    const option = e.currentTarget.getAttribute('data-option-value');

    // Guard against null/undefined option
    if (!option) return;

    if (enterKeyPressed) {
      _handleTravelSelection(option);
    } else {
      travelKeyDownNav(e.keyCode);
    }
  };

  return {
    handleTravelModeButtonClick,
    handleTravelModeButtonKeyDown,
    handleTravelModeOptionClick,
    handleTravelModeOptionKeyDown,
  };
};

export const useDepartureDropdown = ({
  focusRef,
  refList,
  showDropdown,
  setShowDropdown,
  filterState,
  setFilterState,
  toggleTravelModeDropdown,
  syncToRedux,
}: UseDepartureDropdownParams) => {
  // General keyboard nav hook
  const [basicKeyDownNav, dropdownKeyDownNav] = useKeyboardNavigation({
    focusRef,
    refList,
    showDropdown,
    toggleDropdown: setShowDropdown,
  });
  const departureBasicKeyDownNav = basicKeyDownNav;
  const departureKeyDownNav = dropdownKeyDownNav;

  const _handleDepartureSelection = (option: string) => {
    const newState = {
      ...filterState,
      commuteTimeMode: option,
    };
    setFilterState(newState);
    syncToRedux(newState);

    setShowDropdown(false);
  };

  const handleDepartureButtonClick = useYieldCallback(() => {
    setShowDropdown(!showDropdown);
    toggleTravelModeDropdown(false);
  });
  const handleDepartureButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) =>
    departureBasicKeyDownNav(e.keyCode);

  const handleDepartureOptionClick = (e: React.MouseEvent<HTMLLIElement>) => {
    const option = e.currentTarget.getAttribute('data-option-value');

    // Guard against null/undefined option
    if (!option) return;

    _handleDepartureSelection(option);
  };

  const handleDepartureOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    e.stopPropagation();

    const keyCode = e.keyCode;
    const enterKeyPressed = keyCode === 13;
    const option = e.currentTarget.getAttribute('data-option-value');

    // Guard against null/undefined option
    if (!option) return;

    if (enterKeyPressed) {
      _handleDepartureSelection(option);
    } else {
      departureKeyDownNav(e.keyCode);
    }
  };

  return {
    handleDepartureButtonClick,
    handleDepartureButtonKeyDown,
    handleDepartureOptionClick,
    handleDepartureOptionKeyDown,
  };
};

export const useAutocompleteDropdown = ({
  focusRef,
  refList,
  showDropdown,
  toggleDropdown,
}: UseAutocompleteDropdownParams) => {
  const [basicKeyDownNav, dropdownKeyDownNav, resetCurrentIdx] = useKeyboardNavigation({
    focusRef,
    refList,
    showDropdown,
    toggleDropdown,
  });
  const autocompleteBasicKeyDownNav = basicKeyDownNav;
  const autocompleteKeyDownNav = dropdownKeyDownNav;
  const autocompleteResetCurrentIdx = resetCurrentIdx;

  return [autocompleteBasicKeyDownNav, autocompleteKeyDownNav, autocompleteResetCurrentIdx];
};
